/**
 * MightyLocal Customer Capture Widget
 *
 * Embeddable JavaScript widget for capturing customer data on any website
 *
 * Usage:
 * <script>
 *   window.MightyLocalConfig = {
 *     teamId: 'YOUR_TEAM_HASHID', // Required: Your unique team ID
 *     apiUrl: 'https://yourdomain.com/api', // Required: Your MightyLocal API URL
 *     trigger: 'auto', // 'auto' for auto-popup or 'manual' for button click
 *     delay: 3000, // Delay before showing popup (in milliseconds)
 *     showFrequency: 'once' // 'once', 'session', or 'always'
 *   };
 * </script>
 * <script src="https://yourdomain.com/js/mighty-customer-widget.js"></script>
 */

(function () {
    'use strict';

    // Configuration
    const config = window.MightyLocalConfig || {};
    const TEAM_ID = config.teamId;
    const API_URL = config.apiUrl || '/api';
    const TRIGGER = config.trigger || 'auto';
    const DELAY = config.delay || 3000;
    const SHOW_FREQUENCY = config.showFrequency || 'once';
    const STORAGE_KEY = 'mightylocal_widget_shown';
    const TEAM_DATA_CACHE_KEY = `mightylocal_widget_team_data`;
    const TEAM_DATA_CACHE_TTL_MS = 5 * 60 * 1000; /* 5 minutes */

    if (!TEAM_ID) {
        console.error('MightyLocal Widget: teamId is required in MightyLocalConfig');
        return;
    }

    // State
    let teamData = null;
    let modalVisible = false;

    // Check if widget should be shown based on frequency setting
    function shouldShowWidget() {
        if (SHOW_FREQUENCY === 'always') {
            return true;
        }

        const lastShown = localStorage.getItem(STORAGE_KEY);

        if (SHOW_FREQUENCY === 'once' && lastShown) {
            // Check if 7 days have passed since last shown
            const daysSinceShown = (new Date() - new Date(lastShown)) / (1000 * 60 * 60 * 24);
            if (daysSinceShown < 7) {
                return false;
            }
        }

        if (SHOW_FREQUENCY === 'session' && sessionStorage.getItem(STORAGE_KEY)) {
            return false;
        }

        return true;
    }

    // Mark widget as shown
    function markWidgetShown() {
        const timestamp = new Date().toISOString();
        if (SHOW_FREQUENCY === 'once') {
            localStorage.setItem(STORAGE_KEY, timestamp);
        }
        if (SHOW_FREQUENCY === 'session') {
            sessionStorage.setItem(STORAGE_KEY, timestamp);
        }
    }

    function isValidTeamData(data) {
        if (!data || typeof data !== 'object') {
            return false;
        }

        if (!data.name || typeof data.name !== 'string') {
            return false;
        }

        if (!Object.prototype.hasOwnProperty.call(data, 'scannables')) {
            return false;
        }

        if (data.scannables === null || data.scannables === false) {
            return false;
        }

        if (!Array.isArray(data.scannables)) {
            return false;
        }

        return true;
    }

    function loadCachedTeamData() {
        try {
            const cached = localStorage.getItem(TEAM_DATA_CACHE_KEY);
            if (!cached) {
                return null;
            }

            const cacheData = JSON.parse(cached);
            if (!cacheData || typeof cacheData !== 'object') {
                return null;
            }

            const cacheAge = Date.now() - cacheData.timestamp;
            if (cacheAge > TEAM_DATA_CACHE_TTL_MS) {
                return null;
            }

            if (!isValidTeamData(cacheData.data)) {
                return null;
            }

            return cacheData.data;
        } catch (error) {
            return null;
        }
    }

    function cacheTeamData(data) {
        if (!isValidTeamData(data)) {
            return;
        }

        try {
            localStorage.setItem(TEAM_DATA_CACHE_KEY, JSON.stringify({
                timestamp: Date.now(),
                data: data
            }));
        } catch (error) {
            // localStorage not available or full
        }
    }

    // Fetch team configuration from API
    async function fetchTeamData() {
        const cachedData = loadCachedTeamData();
        if (cachedData) {
            teamData = cachedData;
            return teamData;
        }

        try {
            const response = await fetch(`/apps/pages/api/${TEAM_ID}/config`);
            if (!response.ok) {
                throw new Error('Failed to fetch team data');
            }
            teamData = await response.json();
            cacheTeamData(teamData);
            return teamData;
        } catch (error) {
            console.error('MightyLocal Widget: Error fetching team data', error);
            return null;
        }
    }

    // Submit customer data
    async function submitCustomerData(formData) {
        try {
            const response = await fetch(`${API_URL}/widget/${TEAM_ID}/customers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to submit data');
            }

            return { success: true, data };
        } catch (error) {
            console.error('MightyLocal Widget: Error submitting customer data', error);
            return { success: false, error: error.message };
        }
    }

    // Create modal HTML
    function createModal() {
        const colors = teamData.colors || {};
        const primaryColor = colors.primary || '#000000';
        const secondaryColor = colors.secondary || '#ffffff';
        const accentColor = colors.accent || '#0066cc';

        const modalHTML = `
            <div id="mighty-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="mighty-modal-title" style="
                display: none;
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.7);
                z-index: 999999;
                justify-content: center;
                align-items: center;
                animation: mightyFadeIn 0.3s ease-in-out;
            ">
                <div id="mighty-modal-content" style="
                    background: white;
                    border-radius: 12px;
                    max-width: 500px;
                    width: 90%;
                    max-height: 90vh;
                    overflow-y: auto;
                    position: relative;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                    animation: mightySlideUp 0.3s ease-in-out;
                ">
                    <button id="mighty-modal-close" aria-label="Close dialog" style="
                        position: absolute;
                        top: 15px;
                        right: 15px;
                        background: transparent;
                        border: none;
                        font-size: 28px!important;
                        cursor: pointer;
                        color: ${primaryColor};
                        opacity: 0.6;
                        transition: opacity 0.2s;
                        line-height: 1;
                        padding: 0;
                        width: 30px;
                        height: 30px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    ">&times;</button>

                    <div style="padding: 40px 30px;">
                        ${teamData.logo_url ? `
                            <div style="text-align: center; margin-bottom: 10px;">
                                <img src="${teamData.logo_url}" alt="${teamData.name}" style="max-width: 180px; height: auto;">
                            </div>
                        ` : `
                            <h2 style="
                                text-align: center;
                                margin: 0 0 20px 0;
                                font-size: 28px;
                                font-weight: bold;
                                color: ${primaryColor};
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                            ">${teamData.name}</h2>
                        `}

                        <h3 id="mighty-modal-title" style="
                            text-align: center;
                            margin: 0 0 10px 0;
                            font-size: 22px;
                            font-weight: 600;
                            color: ${primaryColor};
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                        ">${teamData.scannables && teamData.scannables.length > 0 ? teamData.scannables[0].title : 'Stay Connected!'}</h3>

                        <p style="
                            text-align: center;
                            margin: 0 0 30px 0;
                            font-size: 12px;
                            color: #666;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                        ">${teamData.scannables && teamData.scannables.length > 0
                ? `Sign up for updates from ${teamData.name} and receive a ${teamData.scannables[0].code_type.toUpperCase()} code that can be used to redeem your offer in-store!`
                : 'Join our community to stay updated'}</p>

                        <form id="mighty-customer-form">
                            <div style="margin-bottom: 15px;">
                                <label for="mighty-first-name" class="mighty-sr-only">First Name</label>
                                <input
                                    type="text"
                                    id="mighty-first-name"
                                    name="first_name"
                                    placeholder="First Name"
                                    required
                                    autocomplete="given-name"
                                    style="
                                        width: 100%;
                                        padding: 14px;
                                        border: 2px solid #e0e0e0;
                                        border-radius: 6px;
                                        font-size: 15px;
                                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                                        box-sizing: border-box;
                                        transition: border-color 0.2s;
                                    "
                                >
                            </div>

                            <div style="margin-bottom: 15px;">
                                <label for="mighty-last-name" class="mighty-sr-only">Last Name</label>
                                <input
                                    type="text"
                                    id="mighty-last-name"
                                    name="last_name"
                                    placeholder="Last Name"
                                    required
                                    autocomplete="family-name"
                                    style="
                                        width: 100%;
                                        padding: 14px;
                                        border: 2px solid #e0e0e0;
                                        border-radius: 6px;
                                        font-size: 15px;
                                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                                        box-sizing: border-box;
                                        transition: border-color 0.2s;
                                    "
                                >
                            </div>

                            <div style="margin-bottom: 15px;">
                                <label for="mighty-email" class="mighty-sr-only">Email Address</label>
                                <input
                                    type="email"
                                    id="mighty-email"
                                    name="email"
                                    placeholder="Email Address"
                                    autocomplete="email"
                                    style="
                                        width: 100%;
                                        padding: 14px;
                                        border: 2px solid #e0e0e0;
                                        border-radius: 6px;
                                        font-size: 15px;
                                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                                        box-sizing: border-box;
                                        transition: border-color 0.2s;
                                    "
                                >
                            </div>

                            <div style="margin-bottom: 15px;">
                                <label for="mighty-phone" class="mighty-sr-only">Phone Number</label>
                                <input
                                    type="tel"
                                    id="mighty-phone"
                                    name="phone"
                                    placeholder="Phone Number"
                                    autocomplete="tel"
                                    style="
                                        width: 100%;
                                        padding: 14px;
                                        border: 2px solid #e0e0e0;
                                        border-radius: 6px;
                                        font-size: 15px;
                                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                                        box-sizing: border-box;
                                        transition: border-color 0.2s;
                                    "
                                >
                            </div>

                            <div style="margin-bottom: 20px;">
                                <label for="mighty-zip-code" class="mighty-sr-only">ZIP Code</label>
                                <input
                                    type="text"
                                    id="mighty-zip-code"
                                    name="zip_code"
                                    placeholder="ZIP Code"
                                    required
                                    maxlength="10"
                                    pattern="[0-9]{5}(-[0-9]{4})?"
                                    title="Please enter a valid ZIP code (e.g., 12345 or 12345-6789)"
                                    autocomplete="postal-code"
                                    style="
                                        width: 100%;
                                        padding: 14px;
                                        border: 2px solid #e0e0e0;
                                        border-radius: 6px;
                                        font-size: 15px;
                                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                                        box-sizing: border-box;
                                        transition: border-color 0.2s;
                                    "
                                >
                            </div>

                            <div style="margin-bottom: 25px;">
                                <label style="
                                    display: flex;
                                    align-items: flex-start;
                                    cursor: pointer;
                                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                                ">
                                    <input
                                        type="checkbox"
                                        id="mighty-opt-in"
                                        name="opt_in"
                                        checked
                                        style="
                                            margin: 4px 10px 0 0;
                                            cursor: pointer;
                                            width: 18px;
                                            height: 18px;
                                            flex-shrink: 0;
                                        "
                                    >
                                    <span style="
                                        font-size: 13px;
                                        color: #666;
                                        line-height: 1.5;
                                    ">I'd like to receive deals, tips, and promotions</span>
                                </label>
                            </div>

                            <div id="mighty-error-message" style="
                                display: none;
                                padding: 12px;
                                margin-bottom: 15px;
                                background: #fee;
                                border: 1px solid #fcc;
                                border-radius: 6px;
                                color: #c33;
                                font-size: 14px;
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                            "></div>

                            <button
                                type="submit"
                                id="mighty-submit-btn"
                                style="
                                    width: 100%;
                                    padding: 16px;
                                    background: ${secondaryColor};
                                    color: white;
                                    border: none;
                                    border-radius: 6px;
                                    font-size: 16px;
                                    font-weight: 600;
                                    cursor: pointer;
                                    transition: all 0.2s;
                                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                                "
                            >SUBMIT</button>

                            <p style="
                                text-align: center;
                                margin: 15px 0 0 0;
                                font-size: 12px;
                                color: #000000;
                                font-weight: bold;
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                            ">
                                <a href="#" id="mighty-no-thanks" style="color: #000000; text-decoration: underline;">No Thanks</a>
                            </p>
                        </form>

                        <div id="mighty-success-message" style="display: none; text-align: center;">
                            <div id="mighty-success-icon" style="
                                font-size: 48px;
                                margin-bottom: 15px;
                            ">✓</div>
                            <h3 id="mighty-success-title" style="
                                margin: 0 0 10px 0;
                                font-size: 22px;
                                font-weight: 600;
                                color: ${primaryColor};
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                            ">Thank You!</h3>
                            <p id="mighty-success-text" style="
                                font-size: 15px;
                                color: #666;
                                margin: 0 0 20px 0;
                                line-height: 1.5;
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                            ">Your information has been saved successfully!</p>
                            <button id="mighty-success-close" style="
                                background: ${secondaryColor};
                                color: white;
                                border: none;
                                padding: 10px 24px;
                                border-radius: 6px;
                                font-size: 14px;
                                font-weight: 600;
                                cursor: pointer;
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                                transition: opacity 0.2s;
                            " onmouseover="this.style.opacity='0.9'" onmouseout="this.style.opacity='1'">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Add animations and accessibility styles
        const style = document.createElement('style');
        style.textContent = `
            @keyframes mightyFadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
            @keyframes mightySlideUp {
                from {
                    transform: translateY(30px);
                    opacity: 0;
                }
                to {
                    transform: translateY(0);
                    opacity: 1;
                }
            }
            #mighty-modal-close:hover {
                opacity: 1 !important;
            }
            #mighty-submit-btn:hover {
                opacity: 0.9;
                transform: translateY(-1px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            }
            #mighty-customer-form input:focus {
                border-color: ${colors.accent || '#0066cc'} !important;
                outline: none;
            }
            /* Screen reader only - visually hidden but accessible */
            .mighty-sr-only {
                position: absolute;
                width: 1px;
                height: 1px;
                padding: 0;
                margin: -1px;
                overflow: hidden;
                clip: rect(0, 0, 0, 0);
                white-space: nowrap;
                border: 0;
            }
        `;
        document.head.appendChild(style);

        // Insert modal into DOM
        document.body.insertAdjacentHTML('beforeend', modalHTML);

        // Attach event listeners
        attachEventListeners();
    }

    // Attach event listeners to modal elements
    function attachEventListeners() {
        const overlay = document.getElementById('mighty-modal-overlay');
        const closeBtn = document.getElementById('mighty-modal-close');
        const noThanks = document.getElementById('mighty-no-thanks');
        const form = document.getElementById('mighty-customer-form');

        // Close modal
        const closeModal = () => {
            overlay.style.display = 'none';
            modalVisible = false;
            // Mark as shown to prevent showing again during this session/period
            markWidgetShown();
        };

        closeBtn.addEventListener('click', closeModal);
        noThanks.addEventListener('click', (e) => {
            e.preventDefault();
            closeModal();
        });

        // Close on overlay click (but not content click)
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                closeModal();
            }
        });

        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modalVisible) {
                closeModal();
            }
        });

        // Track if form has been submitted successfully
        let hasSubmitted = false;

        // Form submission
        form.addEventListener('submit', async (e) => {
            e.preventDefault();

            // Prevent resubmission
            if (hasSubmitted) {
                return;
            }

            const errorDiv = document.getElementById('mighty-error-message');
            const submitBtn = document.getElementById('mighty-submit-btn');
            const formDiv = document.getElementById('mighty-customer-form');
            const successDiv = document.getElementById('mighty-success-message');

            // Get form data
            const formData = {
                first_name: document.getElementById('mighty-first-name').value.trim(),
                last_name: document.getElementById('mighty-last-name').value.trim(),
                email: document.getElementById('mighty-email').value.trim(),
                phone: document.getElementById('mighty-phone').value.trim(),
                zip_code: document.getElementById('mighty-zip-code').value.trim(),
                opt_in: document.getElementById('mighty-opt-in').checked
            };

            // Validate at least email or phone
            if (!formData.email && !formData.phone) {
                errorDiv.textContent = 'Please provide either an email address or phone number.';
                errorDiv.style.display = 'block';
                return;
            }

            // Validate ZIP code format (5 digits or 5+4 format)
            if (formData.zip_code && !/^\d{5}(-\d{4})?$/.test(formData.zip_code)) {
                errorDiv.textContent = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789).';
                errorDiv.style.display = 'block';
                return;
            }

            // Hide error
            errorDiv.style.display = 'none';

            // Disable submit button
            submitBtn.disabled = true;
            submitBtn.textContent = 'Submitting...';

            // Submit data
            const result = await submitCustomerData(formData);

            if (result.success) {
                // Mark as submitted to prevent resubmission
                hasSubmitted = true;

                // Get success message elements
                const successIcon = document.getElementById('mighty-success-icon');
                const successTitle = document.getElementById('mighty-success-title');
                const successText = document.getElementById('mighty-success-text');

                // Check if this was an existing customer update
                if (result.data && result.data.already_registered) {
                    // Show info icon for existing customer (circled i)
                    successIcon.innerHTML = `
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" fill="#2196F3"/>
                            <path d="M11 11H13V17H11V11Z" fill="white"/>
                            <circle cx="12" cy="8" r="1" fill="white"/>
                        </svg>
                    `;
                    successIcon.style.color = '#2196F3'; // Blue for info
                    successTitle.textContent = 'Already Registered!';
                    successText.textContent = result.data.message || "You're already on our list! We've updated your information.";
                } else {
                    // Show checkmark for new customer
                    successIcon.innerHTML = '✓';
                    successIcon.style.color = '#4CAF50'; // Green for success
                    successTitle.textContent = 'Thank You!';
                    successText.textContent = result.data && result.data.message ? result.data.message : 'Your information has been saved successfully!';
                }

                formDiv.style.display = 'none';
                successDiv.style.display = 'block';

                // Mark as shown
                markWidgetShown();

                // Add close button handler
                document.getElementById('mighty-success-close').addEventListener('click', closeModal);

                // Optional: Auto-close after 5 seconds (extended from 3)
                // setTimeout(closeModal, 5000);
            } else {
                // Show error
                errorDiv.textContent = result.error || 'Something went wrong. Please try again.';
                errorDiv.style.display = 'block';

                // Re-enable submit button
                submitBtn.disabled = false;
                submitBtn.textContent = 'Submit';
            }
        });
    }

    // Show modal
    function showModal() {
        if (!shouldShowWidget()) {
            return;
        }

        const overlay = document.getElementById('mighty-modal-overlay');
        if (overlay) {
            overlay.style.display = 'flex';
            modalVisible = true;
        }
    }

    // Initialize widget
    async function init() {
        if (!shouldShowWidget()) {
            return;
        }

        // Fetch team data
        await fetchTeamData();

        if (!teamData) {
            console.error('MightyLocal Widget: Could not load team data');
            return;
        }

        // Create modal
        createModal();

        // Trigger modal based on configuration
        if (TRIGGER === 'auto') {
            setTimeout(showModal, DELAY);
        } else if (TRIGGER === 'manual') {
            // Expose function to manually trigger modal
            window.MightyLocalWidget = {
                show: showModal
            };
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
